import React, { useMemo, useState } from "react";
import { Button, Input } from "reactstrap";

import {
  HARD_VARIANT_LIMIT,
  MAX_DIMENSION_VALUES,
  SOFT_VARIANT_WARNING_LIMIT,
  buildQuickSizeRanges,
  normalizeLookupKey,
} from "../wizardCreateHelpers";
import WizardIcon from "./WizardIcon";

const Swatch = ({ swatch, size = "" }) => (
  <span
    className={`catalog-wizard-swatch${size ? ` catalog-wizard-swatch--${size}` : ""}`}
    style={{ "--catalog-color-swatch-value": swatch }}
    aria-hidden="true"
  />
);

const Step2Variants = ({
  t,
  colors,
  sizes,
  onToggleColor,
  onRemoveColor,
  onAddCustomColor,
  onToggleSize,
  onAddCustomSize,
  onApplySizeRange,
  apparelSizeSystem = "letters",
  onApparelSizeSystemChange,
  cmsColorOptions,
  colorSearchTerm,
  onColorSearchChange,
  recommendedSizes,
  optionGuidance,
  variantCount,
  getSwatch,
}) => {
  const [showCustomSize, setShowCustomSize] = useState(false);
  const [customSize, setCustomSize] = useState("");

  const searchKey = normalizeLookupKey(colorSearchTerm);
  const suggestions = cmsColorOptions.filter(
    (option) =>
      !colors.includes(option.label) &&
      (!searchKey || normalizeLookupKey(option.label).includes(searchKey))
  );
  const colorLimitReached = colors.length >= MAX_DIMENSION_VALUES;
  const sizeLimitReached = sizes.length >= MAX_DIMENSION_VALUES;

  const quickRanges = useMemo(() => buildQuickSizeRanges(recommendedSizes), [recommendedSizes]);
  const activeRangeKey =
    quickRanges.find((range) => {
      const from = recommendedSizes.indexOf(range.from);
      const to = recommendedSizes.indexOf(range.to);
      const presetSelected = sizes.filter((size) => recommendedSizes.includes(size));
      const expected = recommendedSizes.slice(from, to + 1);
      return (
        presetSelected.length === expected.length &&
        expected.every((size) => presetSelected.includes(size))
      );
    })?.key || "";

  const sizeButtons = [
    ...recommendedSizes,
    ...sizes.filter((size) => !recommendedSizes.includes(size)),
  ];
  const showSizeSystem = optionGuidance.sizePresetKey === "apparel" && Boolean(onApparelSizeSystemChange);
  const sizesHidden =optionGuidance.optionMode === "color-only" && !sizes.length;

  const cartesianEstimate = Math.max(colors.length, 1) * Math.max(sizes.length, 1);
  const hasSelection = colors.length > 0 || sizes.length > 0;
  const isOverHard = hasSelection && cartesianEstimate > HARD_VARIANT_LIMIT;
  const isOverSoft = hasSelection && !isOverHard && cartesianEstimate > SOFT_VARIANT_WARNING_LIMIT;

  const matrixText =
    colors.length && sizes.length
      ? t("catalogProductStudio.matrixBoth")
          .replace("{colors}", String(colors.length))
          .replace("{sizes}", String(sizes.length))
      : colors.length
      ? t("catalogProductStudio.matrixColorOnly").replace("{colors}", String(colors.length))
      : sizes.length
      ? t("catalogProductStudio.matrixSizeOnly").replace("{sizes}", String(sizes.length))
      : t("catalogProductStudio.matrixNone");

  const addTypedColor = () => {
    if (!colorSearchTerm.trim() || colorLimitReached) return;
    onAddCustomColor(colorSearchTerm);
    onColorSearchChange("");
  };

  const handleSearchKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (suggestions.length === 1) {
      onToggleColor(suggestions[0].label);
      onColorSearchChange("");
      return;
    }
    addTypedColor();
  };

  const submitCustomSize = () => {
    if (!customSize.trim() || sizeLimitReached) return;
    onAddCustomSize(customSize);
    setCustomSize("");
  };

  return (
    <>
      <div className="catalog-wizard-panel__head">
        <div>
          <h2 className="catalog-wizard-panel__title">{t("catalogProductStudio.step2Title")}</h2>
          <p className="catalog-wizard-panel__copy">{t("catalogProductStudio.step2Copy")}</p>
        </div>
        <div className="catalog-wizard-info">
          <WizardIcon name="info" size={20} className="catalog-wizard-info__svg" />
          <div>
            <strong>{t("catalogProductStudio.wizardHowItWorks")}</strong>
            {t("catalogProductStudio.step2HowCopy")}
          </div>
        </div>
      </div>

      <section className="catalog-wizard-block">
        <h3 className="catalog-wizard-block__title">{t("catalogProductStudio.step2ColorsTitle")}</h3>
        <p className="catalog-wizard-block__copy">{t("catalogProductStudio.step2ColorsCopy")}</p>

        <div className="catalog-wizard-search-row">
          <div className="catalog-wizard-search">
            <WizardIcon name="search" size={18} className="catalog-wizard-search__icon" />
            <Input
              aria-label={t("catalogProductStudio.step2ColorSearchPlaceholder")}
              value={colorSearchTerm}
              onChange={(event) => onColorSearchChange(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={t("catalogProductStudio.step2ColorSearchPlaceholder")}
            />
          </div>
          <button
            type="button"
            className="catalog-wizard-add-button"
            disabled={!colorSearchTerm.trim() || colorLimitReached}
            onClick={addTypedColor}
          >
            <WizardIcon name="plus" size={16} />
            {t("catalogProductStudio.step2AddNewColor")}
          </button>
        </div>

        {colors.length ? (
          <div className="catalog-wizard-color-chips">
            {colors.map((color) => (
              <span key={color} className="catalog-wizard-color-chip">
                <Swatch swatch={getSwatch(color)} size="lg" />
                {color}
                <button
                  type="button"
                  className="catalog-wizard-color-chip__remove"
                  onClick={() => onRemoveColor(color)}
                  aria-label={`${t("catalogProductStudio.step3RemoveColor")}: ${color}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="catalog-wizard-empty mb-3">{t("catalogProductStudio.step2NoColorsSelected")}</p>
        )}

        <div className="catalog-wizard-divider-label">{t("catalogProductStudio.step2SuggestedColors")}</div>
        {suggestions.length ? (
          <div className="catalog-wizard-suggestions">
            {suggestions.map((option) => (
              <button
                key={option.id ?? option.label}
                type="button"
                className="catalog-wizard-suggestion"
                disabled={colorLimitReached}
                onClick={() => {
                  onToggleColor(option.label);
                  onColorSearchChange("");
                }}
              >
                <span
                  className="catalog-wizard-swatch"
                  style={{ "--catalog-color-swatch-value": option.swatch }}
                  aria-hidden="true"
                />
                {option.label}
              </button>
            ))}
          </div>
        ) : (
          <p className="catalog-wizard-empty">
            {searchKey
              ? t("catalogProductStudio.step2NoColorMatch")
              : t("catalogProductStudio.createAddSavedColors")}
          </p>
        )}
      </section>

      <section className="catalog-wizard-block">
        <div className="catalog-wizard-sizes-layout">
          <div>
            <h3 className="catalog-wizard-block__title">{t("catalogProductStudio.step2SizesTitle")}</h3>
            <p className="catalog-wizard-block__copy">
              {sizesHidden
                ? t("catalogProductStudio.step2NoSizesForType")
                : t("catalogProductStudio.step2SizesCopy")}
            </p>
            {sizeButtons.length ? (
              <div className="catalog-wizard-size-grid">
                {sizeButtons.map((size) => {
                  const isSelected = sizes.includes(size);
                  return (
                    <button
                      key={size}
                      type="button"
                      aria-pressed={isSelected}
                      disabled={!isSelected && sizeLimitReached}
                      className={`catalog-wizard-size${isSelected ? " is-selected" : ""}`}
                      onClick={() => onToggleSize(size)}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            ) : null}

            <button
              type="button"
              className="catalog-wizard-link"
              onClick={() => setShowCustomSize((current) => !current)}
            >
              <WizardIcon name="plus" size={14} />
              {t("catalogProductStudio.step2AddCustomSize")}
            </button>
            {showCustomSize ? (
              <div className="catalog-wizard-inline-add">
                <Input
                  aria-label={t("catalogProductStudio.step2AddCustomSize")}
                  value={customSize}
                  placeholder={t("catalogProductStudio.step2CustomSizePlaceholder")}
                  onChange={(event) => setCustomSize(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      submitCustomSize();
                    }
                  }}
                />
                <Button color="secondary" disabled={!customSize.trim() || sizeLimitReached} onClick={submitCustomSize}>
                  {t("catalogProductStudio.step2Add")}
                </Button>
              </div>
            ) : null}
          </div>

          {showSizeSystem || quickRanges.length ? (
            <div>
              {showSizeSystem ? (
                <div className={`catalog-wizard-field${quickRanges.length ? "" : " mb-0"}`}>
                  <label htmlFor="wizard-size-system" className="catalog-wizard-label">
                    {t("catalogProductStudio.step2SizeSystem")}
                  </label>
                  <Input
                    id="wizard-size-system"
                    type="select"
                    value={apparelSizeSystem}
                    onChange={(event) => onApparelSizeSystemChange(event.target.value)}
                  >
                    <option value="letters">{t("catalogProductStudio.step2SizeSystemLetters")}</option>
                    <option value="numeric">{t("catalogProductStudio.step2SizeSystemNumeric")}</option>
                    <option value="jeans">{t("catalogProductStudio.step2SizeSystemJeans")}</option>
                  </Input>
                </div>
              ) : null}
              {quickRanges.length ? (
                <div className="catalog-wizard-field mb-0">
                  <label htmlFor="wizard-quick-range" className="catalog-wizard-label">
                    {t("catalogProductStudio.step2QuickPick")}
                  </label>
                  <Input
                    id="wizard-quick-range"
                    type="select"
                    value={activeRangeKey}
                    onChange={(event) => {
                      const range = quickRanges.find((entry) => entry.key === event.target.value);
                      if (range) onApplySizeRange(range.from, range.to);
                    }}
                  >
                    <option value="">{t("catalogProductStudio.step2QuickPickPlaceholder")}</option>
                    {quickRanges.map((range) => (
                      <option key={range.key} value={range.key}>
                        {range.label}
                      </option>
                    ))}
                  </Input>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>

      <section className="catalog-wizard-block">
        <h3 className="catalog-wizard-block__title">{t("catalogProductStudio.step2SummaryTitle")}</h3>
        <p className="catalog-wizard-block__copy">{t("catalogProductStudio.step2SummaryCopy")}</p>

        <div className="catalog-wizard-tiles">
          <div className="catalog-wizard-tile">
            <WizardIcon name="cube" size={26} className="catalog-wizard-tile__icon" />
            <div>
              <span className="catalog-wizard-tile__value catalog-wizard-tile__value--xl" data-testid="wizard-variant-count">
                {variantCount}
              </span>
              <span className="catalog-wizard-tile__label">
                {variantCount === 1
                  ? t("catalogProductStudio.step2VariantToCreateOne")
                  : t("catalogProductStudio.step2VariantsToCreate")}
              </span>
              <span className="catalog-wizard-tile__meta">{matrixText}</span>
            </div>
          </div>
          <div className="catalog-wizard-tile">
            <WizardIcon name="palette" size={24} className="catalog-wizard-tile__icon" />
            <div>
              <span className="catalog-wizard-tile__value">{colors.length}</span>
              <span className="catalog-wizard-tile__label">{t("catalogProductStudio.step2ColorsSelected")}</span>
              <div className="catalog-wizard-tile__dots">
                {colors.slice(0, 10).map((color) => (
                  <Swatch key={color} swatch={getSwatch(color)} />
                ))}
              </div>
            </div>
          </div>
          <div className="catalog-wizard-tile">
            <WizardIcon name="tag" size={24} className="catalog-wizard-tile__icon" />
            <div>
              <span className="catalog-wizard-tile__value">{sizes.length}</span>
              <span className="catalog-wizard-tile__label">{t("catalogProductStudio.step2SizesSelected")}</span>
              <div className="catalog-wizard-tile__dots">
                {sizes.slice(0, 8).map((size) => (
                  <span key={size} className="catalog-wizard-mini-chip">
                    {size}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className={`catalog-wizard-tile ${isOverHard ? "catalog-wizard-tile--blocked" : "catalog-wizard-tile--ok"}`}>
            <span
              className={`catalog-wizard-status-icon${isOverHard ? " catalog-wizard-status-icon--blocked" : ""}`}
              aria-hidden="true"
            >
              {isOverHard ? "!" : <WizardIcon name="check" size={14} strokeWidth={3} />}
            </span>
            <div>
              <span className="catalog-wizard-tile__title">
                {isOverHard
                  ? t("catalogProductStudio.step2BlockedTitle")
                  : t("catalogProductStudio.step2ReadyTitle")}
              </span>
              <span className="catalog-wizard-tile__copy">
                {isOverHard
                  ? t("catalogProductStudio.step2HardLimitWarning").replace("{count}", String(cartesianEstimate))
                  : t("catalogProductStudio.step2ReadyCopy")}
              </span>
            </div>
          </div>
        </div>

        <p className="catalog-wizard-note">{t("catalogProductStudio.step2SkuAutoNote")}</p>
        {colorLimitReached || sizeLimitReached ? (
          <p className="catalog-wizard-warning">{t("catalogProductStudio.step2DimensionValueLimitWarning")}</p>
        ) : null}
        {isOverSoft ? (
          <p className="catalog-wizard-warning">
            {t("catalogProductStudio.step2SoftLimitWarning").replace("{count}", String(cartesianEstimate))}
          </p>
        ) : null}
      </section>
    </>
  );
};

export default Step2Variants;
